import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sliders, Sparkles, FileDown, Activity, Cpu, ShieldCheck, Volume2, VolumeX, MessagesSquare, TrendingUp, TrendingDown, Calendar, CheckCircle2 } from "lucide-react";
import FortinetNewsTicker from "@/components/FortinetNewsTicker";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const STEPS = [
  {
    num: "01",
    icon: Sliders,
    title: "Saisir les besoins",
    body: "Utilisateurs, sites, cas d'usage, niveau de sécurité, durée.",
  },
  {
    num: "02",
    icon: Sparkles,
    title: "Générer la proposition",
    body: "BOM, BOQ, schéma d'architecture et upsell — instantanément.",
  },
  {
    num: "03",
    icon: FileDown,
    title: "Exporter au client",
    body: "PDF, Excel, CSV — prêts à envoyer, conformes et défendables.",
  },
];

// Looped hero background videos — served locally to avoid hotlink CDN headers
// (Pexels CDN replies with Content-Disposition: attachment which Chrome refuses
// to render in a <video> tag). Source: Pexels — free for commercial use.
const HERO_VIDEOS = [
  "/hero-video-2.mp4",
  "/hero-video.mp4",
];

export default function Landing() {
  const { user } = useAuth();
  const [muted, setMuted] = React.useState(true);
  const videoRef = React.useRef(null);

  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !videoRef.current.muted;
    videoRef.current.muted = next;
    setMuted(next);
    // Restart playback after unmute (autoplay policy)
    if (!next) videoRef.current.play().catch(() => {});
  };

  return (
    <section
      className="relative overflow-hidden bg-zinc-950 text-white"
      data-testid="landing-hero"
    >
      {/* Hero background video */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover opacity-50"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        poster="https://images.pexels.com/photos/4974920/pexels-photo-4974920.jpeg?auto=compress&cs=tinysrgb&w=1920"
        aria-hidden="true"
        data-testid="landing-hero-video"
      >
        {HERO_VIDEOS.map((src) => (
          <source key={src} src={src} type="video/mp4" />
        ))}
      </video>

      {/* Layered dark gradient — keeps text legible on any video frame */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/85 via-zinc-950/65 to-zinc-950/95" />
      {/* Subtle grid + accent glow */}
      <div className="absolute inset-0 hero-grid-bg opacity-[0.07]" />
      <div className="absolute top-0 right-0 w-[640px] h-[640px] rounded-full blur-3xl bg-[#D42B1E]/20 -translate-y-1/3 translate-x-1/4 pointer-events-none" />

      {/* Sound toggle (Pexels videos have no audio but the control reassures users) */}
      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? "Activer le son" : "Couper le son"}
        title={muted ? "Activer le son" : "Couper le son"}
        data-testid="landing-video-mute-toggle"
        className="absolute top-4 right-4 z-20 h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 backdrop-blur border border-white/20 text-white/80 hover:bg-white/20 transition-colors"
      >
        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </button>

      <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 pb-6 grid lg:grid-cols-12 gap-6 items-center">
        {/* LEFT — copy + inline workflow + experts CTA */}
        <div className="lg:col-span-7 text-left">
          <div className="inline-flex items-center gap-2 border border-white/20 rounded-sm px-3 py-1 bg-white/5 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-[#D42B1E]" />
            <span className="text-[11px] font-semibold tracking-[0.18em] text-white/80 uppercase">
              Plateforme propriétaire · Vendor
            </span>
          </div>

          {/* Live status pill — green pulsing dot to signal the app is up.
              Uses tailwind's animate-pulse + a CSS keyframe for a stronger,
              traditional "blink" effect on the dot itself. */}
          <div
            className="ml-2 inline-flex items-center gap-2 border border-emerald-400/40 rounded-sm px-3 py-1 bg-emerald-500/10 backdrop-blur align-middle"
            data-testid="landing-live-badge"
          >
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 pythagore-live-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-[11px] font-semibold tracking-[0.18em] text-emerald-200 uppercase">
              Pythagore is live
            </span>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <img
              src="/pythagore-logo.svg"
              alt="Pythagore"
              className="h-9 sm:h-10 w-auto brightness-0 invert"
              data-testid="landing-hero-logo"
            />
            <span className="text-xs font-semibold tracking-[0.22em] text-white/60 uppercase border-l border-white/20 pl-3 hidden sm:inline">
              BOM / BOQ Engine
            </span>
          </div>

          <h1 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.1]">
            Montez vos BOM et BOQ
            <br className="hidden sm:block" />
            facilement <span className="text-[#D42B1E]">grâce à l'App.</span>
          </h1>

          <p className="mt-3 max-w-xl text-sm text-white/70 leading-snug">
            Pythagore dimensionne, tarife et formate vos propositions
            avant-vente en quelques secondes.
          </p>

          {/* Inline workflow strip — replaces the heavy 3-card section */}
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="landing-workflow-inline">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.num}>
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] text-white/70"
                  data-testid={`landing-step-${s.num}`}
                >
                  <span className="font-mono text-[10px] text-white/40 tracking-[0.1em]">{s.num}</span>
                  <s.icon className="h-3.5 w-3.5 text-[#FCA5A5]" />
                  <span className="font-medium text-white/90">{s.title}</span>
                </span>
                {i < STEPS.length - 1 && <ArrowRight className="h-3 w-3 text-white/20" />}
              </React.Fragment>
            ))}
          </div>

          {/* CTAs */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {user ? (
              <>
                <Button
                  asChild
                  size="lg"
                  className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm h-11 px-6 shadow-lg shadow-[#D42B1E]/30"
                  data-testid="hero-cta-configurator-button"
                >
                  <Link to="/configurator">
                    Lancer le configurateur
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="rounded-sm h-11 px-6 border-white/30 text-white bg-white/5 hover:bg-white/10 hover:text-white"
                  data-testid="hero-cta-quotes-button"
                >
                  <Link to="/quotes">Mes devis</Link>
                </Button>
              </>
            ) : (
              <>
                <Button
                  asChild
                  size="lg"
                  className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm h-11 px-6 shadow-lg shadow-[#D42B1E]/30"
                  data-testid="hero-cta-login-button"
                >
                  <Link to="/login">
                    Se connecter
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="rounded-sm h-11 px-6 border-white/30 text-white bg-white/5 hover:bg-white/10 hover:text-white"
                  data-testid="hero-cta-register-button"
                >
                  <Link to="/register">Créer un compte partenaire</Link>
                </Button>
              </>
            )}
          </div>

          {/* Trust strip + Live KPIs */}
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-white/60" data-testid="landing-trust-strip">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Listes de prix officielles Fortinet</span>
            <span className="inline-flex items-center gap-1.5"><Activity className="h-3.5 w-3.5 text-amber-400" /> Sizing live · ZTNA · SD-WAN</span>
            <span className="inline-flex items-center gap-1.5"><Cpu className="h-3.5 w-3.5 text-[#FCA5A5]" /> 24 710 SKU indexées</span>
          </div>

          {/* Live pricelist KPIs (loaded async) */}
          <PricelistKpiStrip />

          {/* Experts en ligne — inline single line */}
          <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="landing-live-experts">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.14em] text-[#FCA5A5] uppercase">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              Experts en ligne · &lt; 30 min
            </span>
            <Button
              asChild
              size="sm"
              className="bg-emerald-500 hover:bg-emerald-600 text-white border-0 rounded-sm h-8 px-3 text-xs"
              data-testid="landing-experts-cta"
            >
              <Link to={user ? "/contact" : "/register"} className="inline-flex items-center gap-1.5">
                <MessagesSquare className="h-3 w-3" />
                {user ? "Ouvrir l'équipe Fortinet" : "Échanger en direct"}
              </Link>
            </Button>
          </div>
        </div>

        {/* RIGHT — glassmorphism UI mockup */}
        <div className="lg:col-span-5" data-testid="landing-hero-ui-overlay">
          <HeroOverlay />
        </div>
      </div>

      {/* TV-style live news ticker — Fortinet Newsroom feed */}
      <FortinetNewsTicker />
    </section>
  );
}

/**
 * PricelistKpiStrip — async-loaded micro-strip showing 4 numerical KPIs
 * (SKU sortants, hausses, baisses, nouveautés). Replaces the bulky
 * PricelistTeaser block so all KPIs fit in the hero. No-op on error.
 */
function PricelistKpiStrip() {
  const [s, setS] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/pricelist/changes").then((r) => r.data?.summary || null).catch(() => null),
      api.get("/pricelist/base/last-diff?limit=1").then((r) => r.data || null).catch(() => null),
    ]).then(([summary, diff]) => {
      if (!summary && !diff) return;
      const d = diff?.available ? (diff.summary || {}) : {};
      setS({
        eos: summary?.eos_count ?? 0,
        ups: d.ups ?? summary?.price_increase_count ?? 0,
        downs: d.downs ?? 0,
        added: d.added ?? summary?.new_count ?? 0,
      });
    });
  }, []);
  if (!s) return null;
  const KPIS = [
    { value: s.eos, label: "EOS", icon: Calendar, cls: "text-amber-300" },
    { value: s.ups, label: "Hausses", icon: TrendingUp, cls: "text-[#FCA5A5]" },
    { value: s.downs, label: "Baisses", icon: TrendingDown, cls: "text-emerald-300" },
    { value: s.added, label: "Nouveautés", icon: CheckCircle2, cls: "text-emerald-300" },
  ];
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="landing-pricelist-kpis">
      <Link
        to="/login"
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-white/50 hover:text-white"
      >
        Évolutions PL
        <ArrowRight className="h-3 w-3" />
      </Link>
      {KPIS.map((k) => (
        <span
          key={k.label}
          className="inline-flex items-center gap-1.5 text-xs"
          data-testid={`pricelist-kpi-${k.label.toLowerCase()}`}
        >
          <k.icon className={`h-3 w-3 ${k.cls}`} />
          <span className={`font-mono font-bold tabular-nums ${k.cls}`}>{k.value}</span>
          <span className="text-white/50">{k.label}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * HeroOverlay — glassmorphism mockup of the Pythagore configurator,
 * floats above the video to suggest "Pythagore in action" on the
 * young-developer footage. Pure CSS — no extra assets to load.
 */
function HeroOverlay() {
  return (
    <div className="relative" data-testid="hero-overlay-mockup">
      {/* Glow */}
      <div className="absolute -inset-4 rounded-2xl bg-[#D42B1E]/15 blur-2xl" aria-hidden />

      <div className="relative rounded-sm overflow-hidden border border-white/20 bg-white/10 backdrop-blur-2xl shadow-2xl shadow-black/40">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 px-3 py-2 bg-white/10 border-b border-white/10">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
          <span className="ml-3 text-[10px] font-mono text-white/60 tracking-wider truncate">
            pythagore.app/configurator
          </span>
        </div>

        {/* Top status pill */}
        <div className="px-5 pt-5">
          <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-sm bg-emerald-400/15 border border-emerald-400/30 text-[10px] font-semibold tracking-wider uppercase text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Liste de prix à jour · 28 jours
          </div>
        </div>

        {/* Inputs row */}
        <div className="px-5 pt-4 grid grid-cols-3 gap-2 text-[10px]">
          {[
            ["Utilisateurs", "150"],
            ["Sites", "3"],
            ["Cas d'usage", "SD-WAN"],
          ].map(([k, v]) => (
            <div key={k} className="rounded-sm border border-white/10 bg-white/5 px-2 py-1.5">
              <p className="font-mono text-white/50 uppercase tracking-wider text-[9px]">{k}</p>
              <p className="font-semibold text-white">{v}</p>
            </div>
          ))}
        </div>

        {/* BOM rows */}
        <div className="px-5 mt-4 space-y-1.5">
          {[
            { sku: "FG-80F-BDL-811-36", name: "FortiGate-80F · Siège", qty: 1, price: "$3 624" },
            { sku: "FG-60F-BDL-811-36", name: "FortiGate-60F · Agence", qty: 2, price: "$1 906" },
            { sku: "FC1-10-FMGVS-258", name: "FortiManager VM · 3Y", qty: 1, price: "$1 307" },
            { sku: "FC1-10-AZVMS-465", name: "FortiAnalyzer VM · 3Y", qty: 1, price: "$4 098" },
          ].map((r, i) => (
            <div
              key={r.sku}
              className="flex items-center justify-between rounded-sm bg-white/5 border border-white/10 px-2.5 py-1.5 text-[11px]"
              style={{ animation: `slideUp 0.6s ${0.2 + i * 0.1}s both` }}
            >
              <div className="min-w-0">
                <p className="font-mono text-white/60 text-[9px] truncate">{r.sku}</p>
                <p className="text-white truncate">{r.name}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-white/60 text-[10px]">x{r.qty}</span>
                <span className="font-mono font-semibold text-white">{r.price}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Total + status footer */}
        <div className="mt-4 mx-5 mb-5 rounded-sm bg-[#D42B1E]/15 border border-[#D42B1E]/30 px-3 py-2 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#FCA5A5]">Total HT</span>
          <span className="font-mono font-bold text-white text-base">$10 935</span>
        </div>
      </div>
    </div>
  );
}
